import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@ibk/shared-translation';
import { useBenefitContract, useRecharge, useRechargeBankSlip, useRechargePaymentCode } from '../../../hooks';
import { buildBoletoFilename, resolveOrderIdentifier } from '../../../utils/fileNamingUtils';
import { copyPaymentCode, downloadBankSlip, downloadBankSlipPdf, generatePaymentCode, } from '../../../utils/rechargePaymentUtils';
export function useRechargePaymentBolepixPage() {
    const { t } = useTranslation();
    const { rechargeId } = useParams();
    const [alertData, setAlertData] = useState({
        visible: false,
        message: '',
        type: 'success',
    });
    const [isPixAnnotationVisible, setIsPixAnnotationVisible] = useState(true);
    const { contractData, isLoading: isContractLoading } = useBenefitContract();
    const { recharge, isLoading: isRechargeLoading, isError: isRechargeError, } = useRecharge({
        voucherContractId: contractData?.id,
        rechargeId,
    });
    const { paymentCode, isLoading: isPaymentCodeLoading, isError: isPaymentCodeError, fetchPaymentCode, } = useRechargePaymentCode();
    const { fetchBankSlip, bankSlip, isLoading: isBankSlipLoading, isSuccess: isBankSlipSuccess, isError: isBankSlipError, } = useRechargeBankSlip();
    useEffect(() => {
        if (contractData?.id && rechargeId && recharge) {
            fetchPaymentCode({
                voucherContractId: contractData.id,
                voucherContractRechargeId: rechargeId,
            });
        }
    }, [contractData?.id, rechargeId, recharge]);
    const isLoading = isContractLoading || isRechargeLoading || isPaymentCodeLoading;
    const hasError = isRechargeError || isPaymentCodeError;
    const isDataReady = !isLoading && !hasError && Boolean(recharge) && Boolean(paymentCode);
    const companyName = contractData?.companyName ?? '';
    const accrualPeriod = recharge?.recharge?.accrualPeriod ?? '';
    const rechargeName = recharge?.recharge?.name;
    const orderIdForFile = recharge?.recharge?.orderId ?? '';
    useEffect(() => {
        if (isBankSlipSuccess && bankSlip?.result) {
            const filename = buildBoletoFilename(companyName, accrualPeriod, resolveOrderIdentifier(rechargeName, orderIdForFile));
            downloadBankSlipPdf(bankSlip.result, filename, setAlertData, t('benefits.recharge.payment.bolepix.alerts.pdfUnavailable'));
        }
    }, [isBankSlipSuccess, bankSlip, t, companyName, accrualPeriod, rechargeName, orderIdForFile]);
    useEffect(() => {
        if (hasError) {
            setAlertData({
                visible: true,
                message: t('benefits.recharge.payment.bolepix.alerts.paymentLoadingError'),
                type: 'error',
            });
        }
    }, [hasError, t]);
    useEffect(() => {
        if (isBankSlipError) {
            setAlertData({
                visible: true,
                message: t('benefits.recharge.payment.bolepix.alerts.pdfUnavailable'),
                type: 'error',
            });
        }
    }, [isBankSlipError, t]);
    const handleCopyPixCode = async () => {
        await copyPaymentCode({
            paymentCode: paymentCode?.copyAndPaste,
            setAlertData,
            t,
            translationKey: 'benefits.recharge.payment.bolepix.alerts.pixCopied',
        });
    };
    const handleGeneratePixCode = async () => {
        await generatePaymentCode({
            contractId: contractData?.id,
            rechargeId,
            recharge,
            fetchPaymentCode,
            setAlertData,
            t,
            codeField: 'copyAndPaste',
            generatingKey: 'benefits.recharge.payment.bolepix.alerts.generatingPix',
            generatedKey: 'benefits.recharge.payment.bolepix.alerts.pixGenerated',
            notGeneratedKey: 'benefits.recharge.payment.bolepix.alerts.pixNotGenerated',
        });
    };
    const handleDownloadBankslip = async () => {
        downloadBankSlip({
            contractId: contractData?.id,
            rechargeId,
            recharge,
            fetchBankSlip,
        });
    };
    const handleClosePixAnnotation = () => {
        setIsPixAnnotationVisible(false);
    };
    return {
        alertData,
        rechargeId,
        contractData,
        isContractLoading,
        recharge,
        isRechargeLoading,
        isRechargeError,
        paymentCode,
        isPaymentCodeLoading,
        isPaymentCodeError,
        isLoading,
        hasError,
        isDataReady,
        isBankSlipLoading,
        isPixAnnotationVisible,
        setAlertData,
        handleGeneratePixCode,
        handleCopyPixCode,
        handleDownloadBankslip,
        handleClosePixAnnotation,
    };
}
